<template>
  <v-container pt-0>

    <div class="mt-6 page-header">HawkSoft Text Messaging Settings</div>

    <!-- Phone# Order -->
    <div class="headline mt-4 blue--text">Your Number</div>
    <v-layout px-4 pb-4>
      <v-flex>
        <span v-if="!fOrderStatus === 1">
          It can take up to 5 minutes for a number to be assigned. You can check the current status of your order below. Alternatively, you can also return to HawkSoft Text Messaging in the marketplace to see your assigned number later.
        </span>
        <span v-if="fOrderStatus === -1">
          Your previous phone number order failed for that area of zip code. Please re-enter a different Area Code or Zip Code and submit a new order.
          If you are still unable to get a new number, please contact us at insidesales@hawksoft.com or (866)884-4680 option 4.
        </span>
      </v-flex>

      <TSOrderStatus
        v-if="!isLoading"
        :order-status="fOrderStatus"
        :selected-num="selectedNum"
        @update="loadTextingProfile"
      />

      <TSPhoneLookup
        v-if="fOrderStatus === -1"
        :area-code.sync="areaCode"
        :zip-code.sync="zipCode"
        @error="setValidationErr"
      />

      <v-layout v-if="fOrderStatus === -1" justify-end>
        <v-btn color="callToAction" :disabled="!formValid" @click="orderPhoneNumber">
          <span>Submit</span>
        </v-btn>
      </v-layout>
    </v-layout>

    <!-- Opt-in Process -->
    <TSOptIn v-model="optInMode" :msg="optInMessage" @change="setOptInMode" />

    <!-- Help Response -->
    <TSContactNum :contact-num="contactNum" @contactNumberChanged="contactNumberChanged" />

    <!-- Cancellation -->
    <div v-if="fOrderStatus === 1" class="headline mt-0 blue--text">Manage Subscription</div>
    <v-row v-if="fOrderStatus === 1" class="my-3">
      <v-col class="py-0">
        {{ cancelBlurb }}
      </v-col>
    </v-row>
    <v-row v-if="fOrderStatus === 1" class="my-3">
      <v-col class="py-0">
        <router-link :to="{ name: 'TextingCancel' }">
          Cancel HawkSoft Texting
        </router-link>
      </v-col>
    </v-row>

    <!-- Action Buttons -->
    <v-row class="pt-4">
      <v-col cols="auto">
        <v-btn @click="returnToMarketplace()">Return To Marketplace</v-btn>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-btn :disabled="!formValid" color="#FF9E15" @click="saveChanges">Save</v-btn>
      </v-col>
    </v-row>

    <v-snackbar
      v-model="hasError"
      color="error"
      :timeout="3000"
    >
      {{ errorMessage }}
    </v-snackbar>

    <v-snackbar
      v-model="saved"
      color="success"
      :timeout="3000"
      :bottom="true"
    >
      <v-icon dark>check</v-icon>
      Save Successful!
    </v-snackbar>

  </v-container>
</template>

<style lang="scss" scoped>
  .v-label {
    font-size: 14px !important;
  }
  .page-header {
    color: #A5814b;
    font-weight: bold;
    font-size: 1.3rem;
  }
</style>

<script>
import moment from 'moment';
import { mapState, mapActions } from 'vuex';
import numAvailable from '@/utils/isPhoneAvailable.js';

import TSOptIn from './TSOptIn.vue';
import TSOrderStatus from './TSOrderStatus.vue';
import TSContactNum from './TSContactNum.vue';

export default {
  name: 'TextingSetupMod',
  components: {
    TSOptIn,
    TSOrderStatus,
    TSContactNum
  },
  data: () => ({
    errorMessage: '',
    hasError: false,
    saved: false,
    isLoading: false,
    orderStatus: 0,
    selectedNum: '',
    optInMode: 0,
    optInTimeStamp: '',
    userInitials: '',
    optInStatusChanged: false,
    areaCode: '',
    zipCode: '',
    hasValidationErr: false,
    contactNum: '',
    contactNumValid: false
  }),
  computed: {
    ...mapState([
      'agency',
      'featureSwitches'
    ]),
    numberExists () {
      return ((this.selectedNum || null) !== null);
    },
    formValid () {
      if (this.numberExists && this.selectedNum.length === 0) {
        const oneIsPopulated = (this.areaCode.length > 0 || this.zipCode.length > 0);
        const bothArePopulated = (this.areaCode.length > 0 && this.zipCode.length > 0);
        return this.contactNumValid && (oneIsPopulated && !bothArePopulated) && !this.hasValidationErr;
      } else {
        return this.contactNumValid;
      }
    },
    fOrderStatus () {
      if (this.orderStatus === 0 && !this.numberExists) return 0; // pending
      if (this.numberExists || this.orderStatus === 1) return 1; // completed
      if (this.orderStatus === -1 && !this.numberExists) return -1; // failed
      return 0;
    },
    optInMessage () {
      if (this.optInStatusChanged || !this.optInTimeStamp) return '';
      const timestamp = moment.utc(this.optInTimeStamp).local().format('MM/DD/YYYY hh:mm A');
      return `${this.userInitials} - ${timestamp}`;
    },
    cancelBlurb () {
      let rs = 'Active subscriptions can be cancelled at any time';
      if (this.featureSwitches.TextingProductStatusToggle != null && this.featureSwitches.TextingProductStatusToggle) {
        rs += ' to avoid further charges';
      }
      return rs + '.';
    }
  },
  watch: {
    optInMode (prevVal, newVal) {
      if (newVal === null) return;
      if (prevVal !== newVal) this.optInStatusChanged = true;
    }
  },
  async mounted () {
    this.isLoading = true;
    const response = await this.$api.getOrderInfo();
    if (response && response.data && response.data.code === 0) {
      this.orderStatus = response.data.data[0].status;
    } else {
      this.setError('Something went wrong when retrieving your order status.');
    }
    await this.loadTextingProfile();
    this.isLoading = false;
  },
  async created () {
    this.getFeatureSwitches();
  },
  methods: {
    ...mapActions([
      'getFeatureSwitches'
    ]),
    async loadTextingProfile () {
      if ((this.agency || null) === null) return;

      const response = await this.$api.getTextingProfile(this.agency.agencyId);
      if (response.data) {
        const agencyData = response.data.data[0];
        if ((agencyData || null) !== null) {
          this.optInMode = agencyData.optInMode;
          this.selectedNum = ((agencyData.phones || null) !== null) && agencyData.phones.length > 0 ? agencyData.phones[0].phone : undefined;
          this.optInTimeStamp = agencyData.optInTimeStamp;
          this.userInitials = agencyData.optInUserInitials;
          if (agencyData.agencyContactNum !== null && agencyData.agencyContactNum.length === 12) {
            this.contactNum = agencyData.agencyContactNum.substr(2);
            this.contactNumValid = true;
          } else {
            this.contactNumValid = false;
          }
        } else {
          this.setError('Something went wrong when getting your agency information, please try again later.');
        }
      }
    },
    async saveChanges () {
      this.errorMessage = '';
      var timeStamp = this.optInTimeStamp;
      if (this.optInStatusChanged) {
        timeStamp = new Date().toISOString();
      }

      try {
        const response = await this.$api.setOrderInfo(this.agency.name, this.optInMode, timeStamp, this.contactNum);
        if (response.data.code !== 0) {
          if (response.data.message === 'ExternalPhone must be a valid 10-digit NANP phone number') {
            this.contactNumValid = false;
            this.setError(`The 'HELP' contact number does not appear to be a valid phone number`);
          } else {
            this.setError('Something went wrong when setting your agency information, please try again later.');
          }
        } else {
          this.saved = true;
          this.optInTimeStamp = timeStamp;
        }
      } catch (err) {
        this.setError('Something went wrong when setting your agency information, please try again later.');
      }
    },
    async orderPhoneNumber () {
      var searchBy = 0;
      if (this.zipCode.length > 0) {
        searchBy = 1;
      }

      const chkRs = await this.$api.checkNumAvailable(searchBy, this.areaCode, this.zipCode);
      if (numAvailable(chkRs)) {
        const errorMsg = `Phone numbers are unavailable for that ${searchBy === 0 ? 'area' : 'zip'} code, please try a different area code or zip code.`;
        this.setError(errorMsg);
        return;
      }

      const orderRs = await this.$api.orderNumber(searchBy, 1, this.areaCode, this.zipCode);
      if (orderRs.data.code !== 0) {
        const errorMsg = 'Error ordering number, please reach out to HawkSoft customer service to assistance.';
        this.setError(errorMsg);
      }
    },
    setValidationErr (bool) {
      this.hasValidationErr = bool;
    },
    setOptInMode (v) {
      this.optInMode = v;
    },
    setError (msg) {
      this.hasError = true;
      this.errorMessage = msg;
    },
    returnToMarketplace () {
      this.$router.push({ name: 'Marketplace' });
    },
    contactNumberChanged (num, valid) {
      this.contactNumValid = valid;
      if (valid) this.contactNum = num;
    }
  }
};
</script>
